/**
 * StevensWidget (companion widget) — Spec 10-1: Persistent Floating Chat Widget
 *
 * A small always-visible button (bottom-right) that expands into a chat panel.
 * Shares conversation state with /chat page. Includes page-aware contextual
 * chips, the voice session control, and the companion's name.
 *
 * REBUILT ON THE SHARED PRIMITIVES (#900/#903/#904/#905, epic #907). The widget
 * used to carry a private raw-text `MessageList` that never learned markdown and
 * a private `ChatInputBar` with two dead "coming soon" buttons, plus four copies
 * of the panel shell. All three are gone:
 *
 *   transcript → `components/chat/MessageList` (markdown, copy, grouping)
 *   composer   → `components/chat/ChatInput`   (uploads, dictation, voice)
 *   shell      → `./ChatPanelShell`            (one render path, expand, +New)
 *
 * What stays local is what is genuinely widget-shaped: the page-aware chip bar
 * and the two persona branches' thread lifecycles. The parent "voice responses"
 * speaker toggle is gone (#2384): it only wrote a localStorage key nothing
 * read, so it changed nothing, and it showed even with voice switched off.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { useCompanionName } from '@/hooks/useCompanionName';
import { useStevensWidget } from './StevensWidgetContext';
import { ChatPanelShell, useExpandedPanel } from './ChatPanelShell';
import { getChipsForRoute, resolveChipText } from '@/lib/stevens-navigation';
import { MessageList } from '@/components/chat/MessageList';
import { MessageList as CoreMessageList } from '@core/chat/components/MessageList';
import { ChatSurfaceProvider } from '@core/config';
import { useChatConfig } from '@chat-config';
import { toCoreMessages } from '@/features/chat-trf/adapter/messages';
import { renderTrfTurn } from '@/features/chat-trf/turns/renderTrfTurn';
import { ChatErrorRow } from '@/features/chat-trf/ChatErrorRow';
import { useChatVoicePip } from '@/features/chat-trf/voice/useChatVoicePip';
import { getPipVoiceSession } from '@/features/chat-trf/voice/pipVoiceStore';
import { ChatInput } from '@/components/chat/ChatInput';
import { ThreadHistoryControl } from '@/components/chat/ThreadHistoryList';
import { useChat } from '@/hooks/useChat';
import { useWilliamsChat } from '@/hooks/useWilliamsChat';
import { chatApi } from '@/services/chatApi';
import { williamsApi } from '@/services/williamsApi';
import { getLastThreadId, retireActiveThread, setActiveThreadId } from '@/lib/williams-thread';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import { useBottomSheet } from '@/components/layout/BottomSheetContext';
import { useToast } from '@/components/ui/use-toast';
import type { PendingAttachment } from '@/types/chat';
import { serverMessage } from '@/lib/serverMessage';
import { COPILOT_NAME } from '@/lib/companion-config';

// ── Shared sub-components ──────────────────────────────────────────

/** Chips are actions, not messages: the route chips send a prompt, the
 *  "Continue last thread" chip switches conversations. One shape, two uses. */
interface ChipAction {
  label: string;
  onClick: () => void;
  /** A chip that cannot act yet says so instead of swallowing the tap. */
  disabled?: boolean;
}

function ChipBar({ chips }: { chips: ChipAction[] }) {
  if (chips.length === 0) return null;
  return (
    <div className="flex gap-1.5 px-4 py-2 border-t overflow-x-auto">
      {chips.map((chip) => (
        <button
          key={chip.label}
          onClick={chip.onClick}
          disabled={chip.disabled}
          className="shrink-0 rounded-full border bg-muted/50 px-3 py-1.5 text-label text-stevens-ink-3 hover:bg-accent transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

/** The hint ("Type a message or tap a chip below") points at the chip row, so
 *  it shows only when there is a row to point at (#2384). On a route with no
 *  chips the title and the composer placeholder say enough. */
function EmptyTranscript({ companionName, hasChips }: { companionName: string; hasChips: boolean }) {
  const { t } = useTranslation('chat');
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 text-center text-stevens-ink-3">
      <Shield className="h-8 w-8 mb-2 text-stevens-indigo/40" />
      <p className="text-body">
        {t('widget.empty.title', { companion: companionName })}
      </p>
      {hasChips && <p className="text-caption mt-1">{t('widget.empty.hint')}</p>}
    </div>
  );
}

// ── Main widget ────────────────────────────────────────────────────

/**
 * Outer wrapper that avoids mounting heavy hooks (useChat / useWilliamsChat
 * SSE connections) when the widget is hidden (e.g. on /chat or /trfer/williams).
 *
 * Routes by role: staff (TRFer + admin) get the Williams-backed widget that
 * talks to the Williams agent via /api/trfer/williams/* (requireTRFerOrAdmin).
 * Everyone else gets the companion via /api/chat/*. The role check lives at
 * the outer-component level, not inside an inner — picking different inner
 * components is a stable hook choice.
 */
export function StevensWidget() {
  const { hidden } = useStevensWidget();
  const { user } = useAuth();
  if (hidden) return null;
  if (user?.role === 'trfer' || user?.role === 'admin') return <WilliamsWidgetInner />;
  return <StevensWidgetInner />;
}

function StevensWidgetInner() {
  const { t } = useTranslation('chat');
  const { isOpen, prefillText, voiceOnOpen, clearVoiceOnOpen, clearPrefill, open, close, pageContext } = useStevensWidget();
  const companionName = useCompanionName();
  const location = useLocation();
  const { user } = useAuth();
  const { expanded, toggleExpanded } = useExpandedPanel();
  // No-ops for non-parents: the underlying query is `enabled: role === 'parent'`,
  // so helpers and admins get `isMemorial: false, isLoading: false`.
  const { isMemorial, isLoading: modeLoading } = useDashboardMode();
  const { isOpen: bottomSheetOpen } = useBottomSheet();
  // Parents see the mobile BottomNav (h-16 + safe-area-inset) so the Shield
  // FAB must lift above it on mobile. Helpers/others don't see the BottomNav,
  // so their Shield stays at the page-bottom edge.
  const liftForBottomNav = user?.role === 'parent';
  const collapsedBottomClass = liftForBottomNav
    ? 'bottom-[calc(5.25rem+env(safe-area-inset-bottom))] sm:bottom-[calc(1.25rem+env(safe-area-inset-bottom))]'
    : 'bottom-[calc(1.25rem+env(safe-area-inset-bottom))]';

  const chat = useChat('');

  // Create a conversation the first time the widget is opened.
  // useChat('') defers connecting until there's a conversation ID, so we
  // initialize here rather than on mount to avoid an SSE call for users
  // who never open the widget.
  useEffect(() => {
    if (isOpen && !chat.conversationId) {
      chat.startNewConversation();
    }
  }, [isOpen, chat.conversationId, chat.startNewConversation]);

  // Close-with-unsent-prefill drops the prefill: reopening the panel later
  // starts from a clean composer (#929). Already-cleared prefill is a no-op.
  const handleClose = useCallback(() => {
    clearPrefill();
    close();
  }, [clearPrefill, close]);

  // #1731 (F-15): the widget consumes the app-level voice session — the
  // same call /chat shows, floating over every route. Its live turns ride
  // this list; a call ending here re-reads the thread.
  const voicePip = useChatVoicePip(chat.conversationId, chat.reloadMessages);
  const startVoice = voicePip.startVoice;
  const startVoiceHere = useCallback(() => {
    startVoice({ sourceConversationId: chat.conversationId || null });
  }, [startVoice, chat.conversationId]);
  const endVoice = useCallback(() => {
    void getPipVoiceSession().end();
  }, []);

  // Duplex (#1731 phase 5, F-7 / C-27): while a call is live a typed turn
  // belongs to it, not to Clio. A refused publish pauses the composer with
  // the audience line and puts the draft back through `initialValue`. The
  // route chips send through here too — they are typed turns.
  const [restoredDraft, setRestoredDraft] = useState('');
  const { isLive: voiceIsLive, trySendText } = voicePip;
  const handleSend = useCallback((text: string) => {
    setRestoredDraft('');
    const toText = () => {
      chat.sendMessage(text, pageContext);
      // A consumed prefill (sent as-is or edited first) must not re-seed the
      // next open (#929).
      clearPrefill();
    };
    if (!voiceIsLive) {
      toText();
      return;
    }
    void trySendText(text).then((handled) => {
      if (!handled) {
        toText();
        return;
      }
      clearPrefill();
      if (handled === 'refused') setRestoredDraft(text);
    });
  }, [chat, pageContext, clearPrefill, voiceIsLive, trySendText]);

  // Activate voice when opened via voice card on dashboard
  useEffect(() => {
    if (isOpen && voiceOnOpen) {
      startVoiceHere();
      clearVoiceOnOpen();
    }
  }, [isOpen, voiceOnOpen, clearVoiceOnOpen, startVoiceHere]);

  // #1731 (F-15, first half): the widget's companion branch renders the copied
  // core's surface under the same audience config as /chat.
  // #2109 R3-B24: the hook re-resolves on a language switch.
  const surfaceConfig = useChatConfig('companion', { agentName: companionName });
  const coreMessages = useMemo(() => toCoreMessages(chat.messages), [chat.messages]);

  // The chip row belongs to the blank slate and goes away on the first
  // message (#1802, Victor 2026-09-06) — same rule as the /chat strip. The
  // chips teach a family what to ask; once the thread is under way they are
  // clutter above the composer, and EmptyTranscript ("Type a message or tap
  // a chip below") is the only copy that points at them.
  const threadIsEmpty = chat.messages.length === 0 && voicePip.voiceMessages.length === 0;
  // #685/#693 memorial gate on the page chips. Withheld while the family
  // record is in flight as well as in memorial mode — the fail-safe mode is
  // 'active', so offering "Log a symptom" first and retracting it is the
  // version of this bug a bereaved parent actually sees.
  const chips = threadIsEmpty
    ? getChipsForRoute(location.pathname, {
        familyName: pageContext?.family_name,
        isMemorial: modeLoading || isMemorial,
      }).map((chip) => ({
        // #2109 R3-B2: a parent chip's label and the message it sends are
        // catalog descriptors, resolved here so a language switch re-renders.
        label: resolveChipText(t, chip.label),
        onClick: () => handleSend(resolveChipText(t, chip.message)),
      }))
    : [];

  return (
    <ChatSurfaceProvider config={surfaceConfig}>
    <ChatPanelShell
      open={isOpen}
      onOpen={() => open()}
      onClose={handleClose}
      expanded={expanded}
      onToggleExpanded={toggleExpanded}
      onNewConversation={() => { chat.startNewConversation(); }}
      title={companionName}
      headerStatus={voicePip.isLive ? (
        <span className="flex items-center gap-1 text-meta opacity-75 whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-status-ok-fg animate-pulse" />
          {voicePip.pip.state === 'speaking' ? surfaceConfig.copy.voice.states.speaking : surfaceConfig.copy.voice.states.listening}
        </span>
      ) : undefined}
      headerExtras={
        // Thread history for the parent persona (#931). Resume is a plain
        // switchConversation — the companion channel has no session
        // contract; /chat conversations exist from their first message.
        <>
          <ThreadHistoryControl
            personaName={companionName}
            activeId={chat.conversationId}
            getConversations={() => chatApi.getConversations({ limit: 20 })}
            onResume={(id) => { void chat.switchConversation(id); }}
          />
        </>
      }
      launcherOffsetClass={collapsedBottomClass}
      // Hide the collapsed Shield FAB while the BottomNav's More sheet is open
      // (FAB sitting on top of a modal sheet is visually noisy — M2.11).
      launcherHidden={bottomSheetOpen}
      launcherAriaLabel={t('widget.launcher', { companion: companionName })}
      chips={<ChipBar chips={chips} />}
      composer={
        <div className="border-t px-3 py-2.5 pb-safe">
          {voicePip.duplexNotice && voicePip.isLive && (
            <p role="status" className="mb-2 text-caption text-muted-foreground">
              {voicePip.duplexNotice}
            </p>
          )}
          <ChatInput
            onSend={handleSend}
            // C-27: typed input pauses while this live call predates the
            // duplex grant; the draft stays in the box until the call ends.
            disabled={voicePip.duplexPaused}
            isTyping={chat.isTyping}
            placeholder={t('widget.placeholder', { companion: companionName })}
            autoFocus
            // The parent receipt flow lives on /chat, where the reimbursement
            // mini-form it opens has room to render. The widget offers no
            // upload rather than an affordance that leads nowhere.
            attachments="none"
            isVoiceActive={voicePip.isLive}
            voiceState={voicePip.pip.state}
            onVoiceStart={startVoiceHere}
            onVoiceStop={endVoice}
            // The Help-page prefill arrives PRE-TYPED and editable in the
            // draft, not as a one-tap chip (#929). It never auto-sends. A
            // draft refused by the live call comes back the same way.
            initialValue={restoredDraft || prefillText || undefined}
          />
        </div>
      }
    >
      {threadIsEmpty ? (
        <EmptyTranscript companionName={companionName} hasChips={chips.length > 0} />
      ) : (
        <>
          {chat.error && <ChatErrorRow error={chat.error} onDismiss={chat.dismissError} />}
          <CoreMessageList
            messages={voicePip.voiceMessages.length > 0 ? [...coreMessages, ...voicePip.voiceMessages] : coreMessages}
            surface="prose"
            coachName={companionName}
            isTyping={chat.isTyping}
            renderTurn={renderTrfTurn({ conversationId: chat.conversationId, companionName })}
            resetSignal={chat.reanchorKey ?? 0}
            className="px-3"
          />
        </>
      )}
    </ChatPanelShell>
    </ChatSurfaceProvider>
  );
}

// ── Williams branch (TRFer-facing) ────────────────────────────────────
//
// Same shell as StevensWidgetInner but backed by useWilliamsChat /
// williamsApi instead of useChat / chatApi. Williams has no voice flow and no
// action cards; it does have document uploads (#901) and dictation (#902),
// both of which arrive with the shared composer.
//
// THREAD LIFECYCLE (#900/#905, direction doc §6). Staff chat is "fresh by
// default": every open mints a new conversation and the thread the TRFer was
// last in becomes resumable behind a standing "Continue last thread" chip. The
// two ids live in `lib/williams-thread.ts`, which is the single writer of both
// storage keys, so the widget and /trfer/williams cannot drift on the
// retire/resume semantics.

function WilliamsWidgetInner() {
  // The DEFAULT_CHIPS fallback is a parent descriptor pair; resolve it too.
  const { t } = useTranslation('chat');
  const { isOpen, prefillText, clearPrefill, open, close } = useStevensWidget();
  const companionName = useCompanionName();
  const location = useLocation();
  const { expanded, toggleExpanded } = useExpandedPanel();
  const { toast } = useToast();

  // Minted at mount, claimed on first open. Nothing reaches the network until
  // the panel opens: `createSession` fires there and useWilliamsChat holds its
  // SSE connection until `sessionReady`.
  const [initialConversationId] = useState(() => crypto.randomUUID());
  const [sessionReady, setSessionReady] = useState(false);
  const sessionInitRef = useRef(false);
  const [lastThreadId, setLastThreadId] = useState<string | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const attachmentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const chat = useWilliamsChat(initialConversationId, sessionReady);
  const conversationId = chat.conversationId;

  // FRESH ON OPEN. Retire whatever the last mount left active BEFORE claiming
  // the new id, so the previous thread lands in the resumable "last" slot.
  // StrictMode-guarded (B-017 pattern) so we don't fire two parallel
  // POST /williams/session calls with the same id.
  useEffect(() => {
    if (!isOpen) return;
    if (sessionInitRef.current) return;
    sessionInitRef.current = true;

    const retired = retireActiveThread();
    setLastThreadId(retired ?? getLastThreadId());
    setActiveThreadId(initialConversationId);

    williamsApi.createSession(initialConversationId).then(() => {
      setSessionReady(true);
    }).catch((err) => {
      console.error('Failed to create Williams session for widget:', err);
      setSessionReady(true);
    });
  }, [isOpen, initialConversationId]);

  useEffect(() => () => {
    if (attachmentTimerRef.current) clearTimeout(attachmentTimerRef.current);
  }, []);

  const canSend = sessionReady && chat.status === 'connected';

  const handleSend = useCallback((text: string) => {
    chat.sendMessage(text);
    clearPrefill();
  }, [chat, clearPrefill]);

  // Close-with-unsent-prefill drops the prefill (#929) — same contract as the
  // parent branch.
  const handleClose = useCallback(() => {
    clearPrefill();
    close();
  }, [clearPrefill, close]);

  /**
   * Retire the current thread and start a clean one. `switchConversation`
   * disconnects the SSE stream before it reconnects, so retiring mid-stream
   * leaves no orphaned EventSource behind.
   */
  const startFreshThread = useCallback(async () => {
    // A thread with nothing in it is not worth offering back, so it does not
    // take the "last" slot from a thread that has content.
    if (chat.messages.length > 0) {
      setActiveThreadId(conversationId);
      setLastThreadId(retireActiveThread());
    }
    const newId = crypto.randomUUID();
    setActiveThreadId(newId);
    try {
      await williamsApi.createSession(newId);
    } catch (err) {
      console.error('Failed to create Williams session for widget:', err);
    }
    await chat.switchConversation(newId);
  }, [chat, conversationId]);

  /**
   * Resume a past thread — from the "Continue last thread" chip or the header
   * history list (#931). `createSession` is an upsert on the backend
   * (`POST /trfer/williams/session` returns the existing conversation when the
   * caller owns it), so resuming re-establishes the session contract the SSE
   * connect depends on (direction doc §6) rather than creating a duplicate —
   * `switchConversation` alone does not create the session.
   */
  const resumeThread = useCallback(async (resumeId: string) => {
    if (!resumeId || resumeId === conversationId) return;

    if (chat.messages.length > 0) {
      // Swap: the thread being left keeps its place as the resumable one.
      setActiveThreadId(conversationId);
      setLastThreadId(retireActiveThread());
    } else if (resumeId === lastThreadId) {
      // The "last" slot is being consumed and the empty thread being left is
      // not worth offering back — drop the chip rather than pointing it at
      // the thread that is now active.
      setLastThreadId(null);
    }
    setActiveThreadId(resumeId);

    try {
      await williamsApi.createSession(resumeId);
    } catch (err) {
      console.error('Failed to resume Williams session for widget:', err);
    }
    await chat.switchConversation(resumeId);
  }, [chat, conversationId, lastThreadId]);

  const continueLastThread = useCallback(async () => {
    if (!lastThreadId) return;
    await resumeThread(lastThreadId);
  }, [lastThreadId, resumeThread]);

  const handleFileSelect = useCallback(async (file: File) => {
    if (attachmentTimerRef.current) clearTimeout(attachmentTimerRef.current);
    setPendingAttachment({ file, status: 'uploading' });
    try {
      const result = await williamsApi.uploadAttachment(conversationId, file);
      setPendingAttachment({ file, status: 'attached', attachmentId: result.attachment_id });
      // An image is stored but not read. Saying so beats letting the TRFer ask
      // Williams about a document it cannot see (honesty rule).
      if (!result.extracted) {
        toast({
          title: `${result.filename} attached`,
          description: result.note ?? `No text could be read from this file, so ${COPILOT_NAME} can’t quote it.`,
        });
      }
      attachmentTimerRef.current = setTimeout(() => setPendingAttachment(null), 4000);
    } catch (err: unknown) {
      const message = serverMessage(err, 'Couldn’t attach that file. Please try again.');
      setPendingAttachment({ file, status: 'error', error: message });
    }
  }, [conversationId, toast]);

  // Staff widget: the parent memorial register does not apply here. Williams
  // has its own bereavement register (agents/williams/bereavement.py), and it
  // is about how a bereaved family is DESCRIBED to a counselor, not about
  // suppressing a counselor's own tools.
  const routeChips = getChipsForRoute(location.pathname, { isMemorial: false });
  const chips: ChipAction[] = [
    ...(lastThreadId && lastThreadId !== conversationId
      ? [{ label: 'Continue last thread', onClick: () => { void continueLastThread(); } }]
      : []),
    ...routeChips.map((chip) => ({
      label: resolveChipText(t, chip.label),
      // Sending before the SSE handshake would drop the message on the floor,
      // so the chip is visibly unavailable until the stream is up.
      disabled: !canSend,
      onClick: () => handleSend(resolveChipText(t, chip.message)),
    })),
  ];

  return (
    <ChatPanelShell
      open={isOpen}
      onOpen={() => open()}
      onClose={handleClose}
      expanded={expanded}
      onToggleExpanded={toggleExpanded}
      onNewConversation={() => { void startFreshThread(); }}
      title={companionName}
      headerStatus={!sessionReady ? (
        <span className="text-meta opacity-75 whitespace-nowrap">Connecting…</span>
      ) : undefined}
      headerExtras={
        // Thread history for the staff persona (#931). Resume goes through
        // resumeThread, which runs createSession BEFORE the SSE reconnect
        // (§6) and keeps the "Continue last thread" slot bookkeeping honest.
        <ThreadHistoryControl
          personaName={companionName}
          activeId={conversationId}
          getConversations={() => williamsApi.getConversations()}
          onResume={(id) => { void resumeThread(id); }}
        />
      }
      // Williams widget is trfer/admin only; those users don't see the mobile
      // BottomNav, so the Shield stays at the page-bottom edge.
      launcherOffsetClass="bottom-[calc(1.25rem+env(safe-area-inset-bottom))]"
      launcherAriaLabel={`${companionName} is here`}
      chips={<ChipBar chips={chips} />}
      composer={
        <div className="border-t px-3 py-2.5 pb-safe">
          <ChatInput
            onSend={handleSend}
            disabled={!canSend}
            isTyping={chat.isTyping}
            placeholder={`Ask ${companionName}…`}
            autoFocus
            attachments="documents"
            pendingAttachment={pendingAttachment}
            onFileSelect={handleFileSelect}
            onRemoveAttachment={() => setPendingAttachment(null)}
            // Williams has no voice session in the widget.
            hideVoiceButton
            // The family-profile prefill arrives PRE-TYPED and editable, never
            // as an auto-send: the send stays gated on the TRFer pressing send,
            // by which time SSE is up (mirrors the B-017 prefill gate) (#929).
            initialValue={prefillText || undefined}
          />
        </div>
      }
    >
      {chat.messages.length === 0 ? (
        <EmptyTranscript companionName={companionName} hasChips={chips.length > 0} />
      ) : (
        <MessageList
          messages={chat.messages}
          isTyping={chat.isTyping}
          error={chat.error}
          onDismissError={chat.dismissError}
          personaName={companionName}
          conversationId={conversationId}
          density="compact"
        />
      )}
    </ChatPanelShell>
  );
}
